import React, { Fragment } from 'react'
import { memo, MouseEvent, useCallback, useMemo, useState } from 'react'
import { ActionMenu } from 'src/components/shared/ActionMenu'
import tw from 'twin.macro'
import { SensitiveActionModal } from 'src/components/shared/SensitiveActionModal'
import { MenuActionItem } from 'src/interfaces/ui'
import { useModalOpen } from 'src/hooks/useModalOpen'
import { GSIcon } from 'src/components/shared/GSIcon'
import { colors } from 'src/theme'
import { ContractorFile, ContractorFileDocument } from 'src/interfaces/file'
import { useDispatch } from 'react-redux'
import { showToast } from 'src/utils/toast'
import { handleApiError } from 'src/utils/errorHandlers'
import { createAsyncAction } from 'src/utils/reduxUtils'
import {
  DeleteContractorFile,
  DownloadContractorFile,
  FetchContractorFile,
} from 'src/constants/actionTypes'
import { EditContractorFileExpirationDateModal } from './edit-contractor-file-expiration-date/EditContractorFileExpirationDateModal'
import { EditContractorFileNameModal } from './edit-contractor-file-name/EditContractorFileNameModal'
import { Button } from 'src/components/shared/Button'
import { getMimeTypeFromExtension } from 'src/utils/fileDownload'
import UnassignImage from 'src/assets/images/unassign.svg'
import useCheckPermissions from 'src/hooks/useCheckPermissions'
import { UserPermission } from 'src/constants/user'

interface Props {
  contractorId: string
  contractorFile: ContractorFile
  onUpdate(): Promise<void> | void
}

const ContractorFileItemActionsButtonComponent = ({
  contractorId,
  contractorFile,
  onUpdate,
}: Props) => {
  const dispatch = useDispatch()

  const { hasPermission } = useCheckPermissions()

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const {
    isOpen: isEditContractorFileNameModalOpen,
    open: openEditContractorFileNameModal,
    close: closeEditContractorFileNameModal,
  } = useModalOpen()
  const {
    isOpen: isEditContractorFileExpirationDateModalOpen,
    open: openEditContractorFileExpirationDateModal,
    close: closeEditContractorFileExpirationDateModal,
  } = useModalOpen()
  const {
    isOpen: isArchiveContractorFileModalOpen,
    open: openArchiveContractorFileModal,
    close: closeArchiveContractorFileModal,
  } = useModalOpen()

  const handleClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const handleCloseMenu = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const handleClickDownload = useCallback(async () => {
    try {
      const fileDocument: ContractorFileDocument = await createAsyncAction(
        dispatch,
        FetchContractorFile.request({
          contractorId,
          fileId: contractorFile.id,
        }),
      )

      const buffer: ArrayBuffer = await createAsyncAction(
        dispatch,
        DownloadContractorFile.request({
          contractorId,
          fileId: contractorFile.id,
        }),
      )
      const blob = new Blob([buffer], {
        type: getMimeTypeFromExtension(fileDocument.document.extension || 'pdf'),
      })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `${fileDocument.document.fileName}`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (err) {
      handleApiError(err)
    }
  }, [contractorId])

  const handleSubmitArchive = useCallback(async () => {
    try {
      await createAsyncAction(
        dispatch,
        DeleteContractorFile.request({
          contractorId,
          fileId: contractorFile.id,
        }),
      )

      showToast('File has been archived!')

      closeArchiveContractorFileModal()
      await onUpdate()
    } catch (err) {
      handleApiError(err)
    }
  }, [contractorId, contractorFile.id, onUpdate])

  const actionItems: Array<MenuActionItem> = useMemo(() => {
    return [
      {
        label: 'Download',
        icon: <GSIcon iconName="gs_circle_download" width={20} />,
        onClick: () => {
          handleClickDownload()
          setAnchorEl(null)
        },
      },
      ...(hasPermission(UserPermission.ContractorFilesEdit)
        ? [
            {
              label: 'Set expiration date',
              icon: <GSIcon iconName="gs_calendar_edit" width={20} />,
              onClick: () => {
                openEditContractorFileExpirationDateModal()
                setAnchorEl(null)
              },
            },
            {
              label: 'Edit name',
              icon: <GSIcon iconName="gs_pencil" width={20} />,
              onClick: () => {
                openEditContractorFileNameModal()
                setAnchorEl(null)
              },
            },
          ]
        : []),
      ...(hasPermission(UserPermission.ContractorFilesDelete)
        ? [
            {
              label: 'Archive',
              icon: <GSIcon iconName="gs_trash_can" width={20} />,
              color: colors.COLORS_RED_500,
              onClick: () => {
                openArchiveContractorFileModal()
                setAnchorEl(null)
              },
            },
          ]
        : []),
    ]
  }, [handleClickDownload, hasPermission])

  const isMenuOpen = useMemo(() => Boolean(anchorEl), [anchorEl])

  return (
    <Fragment>
      <Button
        color="grey"
        size="small"
        leftIcon={<GSIcon iconName="gs_dot_3" css={tw`self-center`} width={20} height={20} />}
        onClick={handleClick}
      />
      <ActionMenu
        anchorEl={anchorEl}
        items={actionItems}
        isOpen={isMenuOpen}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        itemClassName={tw`w-[187px]`}
        onClose={handleCloseMenu}
      />
      <EditContractorFileExpirationDateModal
        isOpen={isEditContractorFileExpirationDateModalOpen}
        contractorId={contractorId}
        contractorFile={contractorFile}
        onClose={closeEditContractorFileExpirationDateModal}
        onUpdate={onUpdate}
      />
      <EditContractorFileNameModal
        isOpen={isEditContractorFileNameModalOpen}
        contractorId={contractorId}
        contractorFile={contractorFile}
        onClose={closeEditContractorFileNameModal}
        onUpdate={onUpdate}
      />
      <SensitiveActionModal
        isOpen={isArchiveContractorFileModalOpen}
        title="Archive file"
        text={`Are you sure you want to archive “${contractorFile.name}”? You'll lose access to it from the dashboard.`}
        submitLabel="Archive"
        image={<img src={UnassignImage} />}
        className={tw`w-[298px]`}
        onClose={closeArchiveContractorFileModal}
        onSubmit={handleSubmitArchive}
      />
    </Fragment>
  )
}

export const ContractorFileItemActionsButton = memo(ContractorFileItemActionsButtonComponent)
